import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { 
  Layout, 
  Plus, 
  Edit3, 
  Trash2, 
  Save, 
  X, 
  ChevronDown, 
  ChevronUp,
  Copy,
  Check,
  RefreshCw,
  Eye,
  EyeOff,
  AlertCircle,
  Loader2,
  Search,
  Filter
} from 'lucide-react';
import axios from 'axios';

const TemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showPreview, setShowPreview] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedTemplates, setExpandedTemplates] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'General',
    content: '',
    description: '',
    is_required: false
  });
  
  const backendUrl = '';

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
  }, [selectedCategory]); // eslint-disable-line

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const params = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
      const response = await axios.get(`${backendUrl}/api/admin/templates/${params}`);
      // Auto-sort: Section numbers first (ascending), then TBD/unnumbered at bottom
      const sorted = [...response.data].sort((a, b) => {
        const getOrder = (name) => {
          const match = name.match(/^Section\s+(\d+)/i);
          if (match) return parseFloat(match[1]);
          if (name.startsWith('TBD')) return 1000;
          return 999;
        };
        return getOrder(a.name) - getOrder(b.name);
      });
      setTemplates(sorted);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/templates/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim() || !formData.content.trim()) {
      alert('Please enter a name and content for the template');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${backendUrl}/api/admin/templates/`, formData);
      console.log('Create response:', response.data);
      setIsCreating(false);
      setFormData({ name: '', category: 'General', content: '', description: '', is_required: false });
      fetchTemplates();
      fetchCategories();
    } catch (error) {
      console.error('Error creating template:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert('Failed to create template: ' + errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim() || !formData.content.trim()) {
      alert('Please enter a name and content for the template');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.put(`${backendUrl}/api/admin/templates/${editingTemplate}`, formData);
      console.log('Update response:', response.data);
      setEditingTemplate(null);
      setFormData({ name: '', category: 'General', content: '', description: '', is_required: false });
      fetchTemplates();
      fetchCategories();
    } catch (error) {
      console.error('Error updating template:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert('Failed to update template: ' + errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId, templateName) => {
    if (!window.confirm(`Are you sure you want to delete "${templateName}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${backendUrl}/api/admin/templates/${templateId}`);
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    }
  };

  const handleResetDefaults = async () => {
    if (!window.confirm('This will delete ALL templates and restore defaults. Are you sure?')) {
      return;
    }
    
    try {
      await axios.post(`${backendUrl}/api/admin/templates/reset-defaults`);
      fetchTemplates();
      fetchCategories();
      alert('Templates reset to defaults');
    } catch (error) {
      console.error('Error resetting templates:', error);
      alert('Failed to reset templates');
    }
  };

  const startEdit = (template) => {
    setEditingTemplate(template.id);
    setFormData({
      name: template.name,
      category: template.category,
      content: template.content,
      description: template.description,
      is_required: template.is_required
    });
    setIsCreating(false);
  };

  const startCreate = () => {
    setIsCreating(true);
    setEditingTemplate(null);
    setFormData({ name: '', category: 'General', content: '', description: '', is_required: false });
  };

  const cancelEdit = () => {
    setEditingTemplate(null);
    setIsCreating(false);
    setFormData({ name: '', category: 'General', content: '', description: '', is_required: false });
  };

  const copyToClipboard = async (templateId, content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(templateId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const toggleExpanded = (templateId) => {
    setExpandedTemplates(prev => ({
      ...prev,
      [templateId]: !prev[templateId]
    }));
  };

  const togglePreview = (templateId) => {
    setShowPreview(prev => ({
      ...prev,
      [templateId]: !prev[templateId]
    }));
  };

  const filteredTemplates = templates.filter(template => 
    template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryColor = (category) => {
    const colors = {
      'Headers': 'bg-purple-100 text-purple-700',
      'Sections': 'bg-blue-100 text-blue-700',
      'Signatures': 'bg-green-100 text-green-700',
      'Legal': 'bg-red-100 text-red-700',
      'About': 'bg-orange-100 text-orange-700',
      'General': 'bg-gray-100 text-gray-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
              <Layout className="text-white" size={20} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Template Manager</h3>
              <p className="text-sm text-gray-500">Create, edit, and manage article templates</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={startCreate}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:shadow-lg transition-all"
              data-testid="create-template-btn"
            >
              <Plus size={18} />
              <span>New Template</span>
            </button>
            <button
              onClick={handleResetDefaults}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              title="Reset to default templates"
            >
              <RefreshCw size={18} />
              <span>Reset Defaults</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mt-6 pt-6 border-t border-gray-100">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search templates..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
              data-testid="search-templates"
            />
          </div>
          
          {/* Category Filter */}
          <div className="flex items-center space-x-2">
            <Filter size={16} className="text-gray-400" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
              data-testid="category-filter"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          
          <p className="text-sm text-gray-500">
            {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Create/Edit Form */}
      {(isCreating || editingTemplate) && (
        <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-purple-200">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-lg font-bold text-gray-900">
              {isCreating ? 'Create New Template' : 'Edit Template'}
            </h4>
            <button onClick={cancelEdit} className="p-2 hover:bg-gray-100 rounded-lg">
              <X size={20} className="text-gray-500" />
            </button>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">Template Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Copyright Notice (2025)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                  data-testid="template-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                  data-testid="template-category-select"
                >
                  <option value="General">General</option>
                  <option value="Headers">Headers</option>
                  <option value="Sections">Sections</option>
                  <option value="Signatures">Signatures</option>
                  <option value="Legal">Legal</option>
                  <option value="About">About</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this template"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900"
                  data-testid="template-description-input"
                />
              </div>
              
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="is_required"
                  checked={formData.is_required}
                  onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <label htmlFor="is_required" className="text-sm font-medium text-gray-900">
                  Required template (shown in quality analysis)
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Content (HTML) *</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows="10"
                placeholder="<h3>Section Title</h3>
<p>Your template content here...</p>"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm text-gray-900"
                data-testid="template-content-input"
              />
            </div>
          </div>
          
          {/* Preview */}
          {formData.content && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <h5 className="text-sm font-medium text-gray-900 mb-2">Preview:</h5>
              <div 
                className="p-4 bg-gray-50 rounded-lg prose prose-sm max-w-none text-gray-900"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formData.content) }}
              />
            </div>
          )}
          
          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-100">
            <button
              onClick={cancelEdit}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={isCreating ? handleCreate : handleUpdate}
              disabled={saving}
              className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50"
              data-testid="save-template-btn"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
              <span>{isCreating ? 'Create Template' : 'Save Changes'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Templates List */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="animate-spin mx-auto text-purple-500 mb-4" size={32} />
            <p className="text-gray-500">Loading templates...</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="p-12 text-center">
            <Layout className="mx-auto text-gray-300 mb-4" size={48} />
            <h3 className="text-xl font-bold text-gray-700 mb-2">No Templates Found</h3>
            <p className="text-gray-500 mb-4">
              {searchQuery ? 'Try a different search term' : 'Create your first template to get started'}
            </p>
            <button
              onClick={startCreate}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Create Template
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredTemplates.map((template) => (
              <div key={template.id} className="hover:bg-gray-50 transition-colors">
                {/* Template Header */}
                <div className="px-6 py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="font-semibold text-gray-900">{template.name}</h4>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${getCategoryColor(template.category)}`}>
                          {template.category}
                        </span>
                        {template.is_required && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700">
                            Required
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{template.description}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Updated: {new Date(template.updated_at).toLocaleDateString()}
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => toggleExpanded(template.id)}
                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                        title="Expand/Collapse"
                      >
                        {expandedTemplates[template.id] ? (
                          <ChevronUp size={18} className="text-gray-500" />
                        ) : (
                          <ChevronDown size={18} className="text-gray-500" />
                        )}
                      </button>
                      <button
                        onClick={() => copyToClipboard(template.id, template.content)}
                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                        title="Copy to clipboard"
                      >
                        {copiedId === template.id ? (
                          <Check size={18} className="text-green-500" />
                        ) : (
                          <Copy size={18} className="text-gray-500" />
                        )}
                      </button>
                      <button
                        onClick={() => startEdit(template)}
                        className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Edit template"
                        data-testid={`edit-template-${template.id}`}
                      >
                        <Edit3 size={18} className="text-blue-500" />
                      </button>
                      <button
                        onClick={() => handleDelete(template.id, template.name)}
                        className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                        title="Delete template"
                        data-testid={`delete-template-${template.id}`}
                      >
                        <Trash2 size={18} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Expanded Content */}
                {expandedTemplates[template.id] && (
                  <div className="px-6 pb-4 bg-gray-50 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-3 pt-4">
                      <span className="text-sm font-medium text-gray-700">Content:</span>
                      <button
                        onClick={() => togglePreview(template.id)}
                        className="flex items-center space-x-1 text-sm text-purple-600 hover:text-purple-700"
                      >
                        {showPreview[template.id] ? (
                          <>
                            <EyeOff size={14} />
                            <span>Show Code</span>
                          </>
                        ) : (
                          <>
                            <Eye size={14} />
                            <span>Show Preview</span>
                          </>
                        )}
                      </button>
                    </div>
                    
                    {showPreview[template.id] ? (
                      <div 
                        className="p-4 bg-white rounded-lg border border-gray-200 prose prose-sm max-w-none text-gray-900"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template.content) }}
                      />
                    ) : (
                      <pre className="p-4 bg-gray-800 text-gray-100 rounded-lg overflow-x-auto text-sm">
                        <code>{template.content}</code>
                      </pre>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateManager;
